import { ConfirmModal } from '@vrplatform/components';
import { useDebouncedValue, useId } from '@mantine/hooks';
import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import {
  showLoadingNotification,
  showWarnNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import { useMemo } from 'react';
import { AddBankTransferModal } from '../../views/reconciliation/bank-accounts/list/AddBankTransferModal';
import {
  getEditableBankTransfer,
  type EditableBankTransferSource,
} from '../../views/reconciliation/bank-accounts/list/AddBankTransferModal.utils';

interface ModalProps {
  opened: boolean;
  closeModal: () => void;
  bankTransfer: Maybe<BankTransferModalRecord>;
}

type BankTransferModalRecord = EditableBankTransferSource & {
  bankRecord?: {
    id?: string | null;
  } | null;
  bankRecords?: { id: string }[] | null;
  payment?: {
    date?: string | null;
    bankRecordIds?: string[] | null;
  } | null;
};

const BANK_TRANSFER_DRAWER_MODAL_Z_INDEX = 300;

export const BankTransferEllipsisMenuModals = ({
  deleteModal,
  editModal,
  unreconcileModal,
  bankTransfer: initial,
}: {
  bankTransfer: ModalProps['bankTransfer'];
  deleteModal: {
    opened: boolean;
    closeModal: () => void;
  };
  editModal: {
    opened: boolean;
    closeModal: () => void;
  };
  unreconcileModal: {
    opened: boolean;
    closeModal: () => void;
  };
}) => {
  const [debounced] = useDebouncedValue(initial, 500);

  const bankTransfer = initial || debounced;
  const editableBankTransfer = useMemo(
    () => (bankTransfer ? getEditableBankTransfer(bankTransfer) : null),
    [bankTransfer]
  );

  return (
    <>
      <Delete {...deleteModal} bankTransfer={bankTransfer} />
      <Unreconcile {...unreconcileModal} bankTransfer={bankTransfer} />
      <AddBankTransferModal
        {...editModal}
        bankTransfer={editableBankTransfer}
        zIndex={BANK_TRANSFER_DRAWER_MODAL_Z_INDEX}
      />
    </>
  );
};

const getBankTransferBankRecordId = (
  bankTransfer: Maybe<BankTransferModalRecord>
) =>
  bankTransfer?.bankRecord?.id ??
  bankTransfer?.payment?.bankRecordIds?.[0] ??
  bankTransfer?.bankRecords?.[0]?.id;

const Delete = ({ opened, closeModal, bankTransfer }: ModalProps) => {
  const bankTransferId = bankTransfer?.id;
  const invalidate = useInvalidateApiQueries();

  const { mutateAsync: mutate, isPending: loading } = useApiMutation(
    'delete',
    '/transactions/{id}'
  );

  const submit = async () =>
    await mutate({
      params: {
        path: {
          id: bankTransferId!,
        },
        query: {
          onLocked: 'error',
        },
      },
    })
      .then(() => {
        invalidate(['bank-transfers']);
        invalidate(['accounts']);
        invalidate(['owner-statements']);
        closeModal();
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'Failed to delete bank transfer',
          defaultMessage:
            'We failed to delete the bank transfer. Please try again and contact support if the issue persists.',
        });
      });

  return (
    <ConfirmModal
      type="delete"
      zIndex={BANK_TRANSFER_DRAWER_MODAL_Z_INDEX}
      opened={opened}
      loading={loading}
      closeModal={closeModal}
      onSubmit={submit}
      title={'Delete this bank transfer?'}
      subtitle={
        'Are you sure you want to delete this bank transfer? This action cannot be undone.'
      }
    />
  );
};

const Unreconcile = ({ opened, closeModal, bankTransfer }: ModalProps) => {
  const notifyId = useId();
  const bankRecordId = getBankTransferBankRecordId(bankTransfer);
  const invalidate = useInvalidateApiQueries([
    'bank-records',
    'bank-transfers',
    'accounts',
  ]);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'put',
    '/bank-records/{id}'
  );

  const submit = async () => {
    if (!bankRecordId) {
      showWarnNotification({ message: 'Missing bank record' });
      return;
    }

    showLoadingNotification({ id: notifyId });

    await mutateAsync({
      params: {
        path: {
          id: bankRecordId,
        },
      },
      body: {
        reconciledTransactionIds: [],
      },
    })
      .then(() => {
        updateSuccessNotification({
          id: notifyId,
          title: 'Success!',
          message: 'Bank transfer was unmatched successfully.',
        });
        closeModal();
        invalidate();
      })
      .catch((error: any) => {
        showApiErrorNotification({
          id: notifyId,
          error,
          title: 'Failed to unmatch bank transfer',
          defaultMessage:
            'We failed to unmatch the bank transfer. Please try again later and if the problem persists, contact support.',
        });
      });
  };

  return (
    <ConfirmModal
      type="confirm"
      zIndex={BANK_TRANSFER_DRAWER_MODAL_Z_INDEX}
      opened={opened}
      loading={loading}
      closeModal={closeModal}
      onSubmit={submit}
      title={'Are you sure you want to unmatch this bank transfer?'}
      subtitle={
        'This action will remove the bank transfer from the bank record and allow you to match it again.'
      }
      submitTitle="Yes, unmatch"
    />
  );
};
